import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {CustomScrollingService} from '@app/services/custom-scrolling.service';
import {NavigationLink} from './navigation-link';
import {Observable} from 'rxjs';
import {ProductDetailsNavigationLinkService} from './product-details-navigation-link.service';
import {CurrentProductService} from '@spartacus/storefront';
import {Product} from '@spartacus/core';
import {HtmlElementService} from '@app/services/html-element.service';
import {StickyHeaderService} from '@app/layout/header/services/sticky-header.service';
import {isPlatformBrowser} from '@angular/common';
import {ProductPageDecoratorService} from '../../services/product-page-decorator.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

interface ExtendedNavigationLink extends NavigationLink {
  sectionElement: HTMLElement;
  active: boolean;
}

@UntilDestroy()
@Component({
  selector: 'dcp-navigation-bar',
  templateUrl: './navigation-bar.component.html',
  styleUrls: ['./navigation-bar.component.scss']
})
export class NavigationBarComponent implements OnInit, OnDestroy {
  product: Product;
  navigationLinks$: Observable<NavigationLink[]>;
  navigationLinks: ExtendedNavigationLink[] = [];
  product$: Observable<Product>;
  lastActiveIndex = -1;
  isSticky: boolean = true;
  private scrollCallback: () => void;
  private defaultHeaderOffset: number = 70;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    public productPageDecoratorService: ProductPageDecoratorService,
    protected changeDetector: ChangeDetectorRef,
    private customScrollingService: CustomScrollingService,
    private productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    private currentProductService: CurrentProductService,
    private htmlElementService: HtmlElementService,
    private stickyHeaderService: StickyHeaderService
  ) {
  }

  scrollToElement(selector: string) {
    this.customScrollingService.scrollToElementId(selector, this.defaultHeaderOffset);
  }

  ngOnInit(): void {
    this.navigationLinks$ = this.productDetailsNavigationLinkService.getNavigationLinks$();
    this.product$ = this.currentProductService.getProduct();
    this.scrollCallback = this.setCurrentActiveSectionCallback();

    this.currentProductService.getProduct().pipe(untilDestroyed(this)).subscribe(product => {
      this.product = product;
    });

    if (isPlatformBrowser(this.platformId)) {
      this.navigationLinks$.pipe(untilDestroyed(this)).subscribe(value => {
        this.navigationLinks = value as ExtendedNavigationLink[];
        for (const navigationLink of this.navigationLinks) {
          navigationLink.sectionElement = document.getElementById(navigationLink.id);
        }
        this.lastActiveIndex = -1;
        this.scrollCallback();
        this.changeDetector.detectChanges();
      });
      document.documentElement.classList.add('default-partner-navigation-bar-exists');
      window.addEventListener('scroll', this.scrollCallback, {passive: true});
      this.stickyHeaderService.enableStickyOffset();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.scrollCallback);
      document.documentElement.classList.remove('default-partner-navigation-bar-exists');
      this.stickyHeaderService.disableStickyOffset();
    }
  }

  setCurrentActiveSectionCallback(): any {
    const component: NavigationBarComponent = this;
    return () => {
      if (isPlatformBrowser(this.platformId)) {
        this.setSticky();
        if (component.navigationLinks && component.navigationLinks.length > 0) {
          const activeIndex: number = component.getActiveSection();
          if (activeIndex >= 0 && activeIndex !== component.lastActiveIndex) {
            component.navigationLinks.forEach(elem => {
              elem.active = false;
            });
            component.navigationLinks[activeIndex].active = true;
            component.changeDetector.detectChanges();
            component.lastActiveIndex = activeIndex;
          }
        }
      }
    };
  }

  private setSticky(): void {
    if (document.documentElement.scrollTop > 40) {
      if (!this.isSticky) {
        this.isSticky = true;
        this.changeDetector.detectChanges();
      }
    } else if (document.documentElement.scrollTop < 10) {
      if (this.isSticky) {
        this.isSticky = false;
        this.changeDetector.detectChanges();
      }
    }
  }

  getActiveSection(): number {
    if (isPlatformBrowser(this.platformId)) {
      const pageHeight: number = window.innerHeight;
      const pageOffset: number = this.htmlElementService.documentOffset();
      const pageSize: number = document.body.scrollHeight;
      if (!!this.navigationLinks && this.navigationLinks.length > 0) {
        if (pageOffset <= 10) {
          return 0;
        }
        if (pageOffset + pageHeight + 10 >= pageSize) {
          return this.navigationLinks.length - 1;
        }
        const section = this.getSection(pageOffset, pageHeight);
        if (section >= 0) {
          return section;
        }
      }
    }
    return -1;
  }

  private getSection(pageOffset, pageHeight) {
    for (let i: number = this.navigationLinks.length - 1; i >= 0; i--) {
      if (this.navigationLinks[i] && this.navigationLinks[i].sectionElement) {
        const offset: number = this.navigationLinks[i].sectionElement.offsetTop;
        const height: number = this.htmlElementService.getAbsoluteHeight(this.navigationLinks[i].sectionElement);
        if (pageOffset + (pageHeight / 2) >= offset && pageOffset + (pageHeight / 2) <= offset + height) {
          return i;
        }
      }
    }
  }
}
